import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress, FormControlLabel, Grid,
  MenuItem, Slider, Stack, Switch, TextField, Typography,
} from '@mui/material';
import {
  ASSET_CLASSES, CLASS_LABEL, DISCLAIMER, investmentPlanService,
  type AllocationResponse, type AssetClass, type PlanAccountRule,
} from '../services/investment-plan-service';
import {
  EMPTY_FORM, formToPlan, planToForm, targetSum, validatePlanForm, type PlanForm,
} from '../utils/investment-plan';
import { AllocationBars, DCAStatusLine } from '../components/plan/AllocationBars';
import { StreamsCard } from '../components/plan/StreamsCard';
import { RemindersCard } from '../components/plan/RemindersCard';
import { baseMoney, useBaseCurrency } from '../stores/currency-store';

/**
 * Planning → Investment plan (issue #31).
 *
 * The household writes down its target mix, rebalance band, cash floor and
 * DCA glide path; FolioWorth checks the real portfolio against it every night
 * and RECOMMENDS. It never trades. Every save is a new plan version, and each
 * recommendation cites the version that produced it.
 */
export const InvestmentPlanPage: React.FC = () => {
  useBaseCurrency();
  const [form, setForm] = useState<PlanForm>(EMPTY_FORM);
  const [rules, setRules] = useState<PlanAccountRule[]>([]);
  const [version, setVersion] = useState(0);
  const [alloc, setAlloc] = useState<AllocationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ tone: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [checkSummary, setCheckSummary] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, a] = await Promise.all([investmentPlanService.getPlan(), investmentPlanService.allocation()]);
      setForm(planToForm(p.enabled, p.plan));
      setRules(p.plan?.accounts ?? []);
      setVersion(p.version);
      setAlloc(a);
    } catch (e: any) {
      setMessage({ tone: 'error', text: e?.message ?? 'Failed to load the plan' });
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const errors = useMemo(() => validatePlanForm(form), [form]);
  const sum = targetSum(form.targets);
  const valid = Object.keys(errors).length === 0;
  const set = (patch: Partial<PlanForm>) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    if (!valid) return;
    setSaving(true);
    setMessage(null);
    try {
      const res = await investmentPlanService.savePlan(form.enabled, formToPlan(form, rules));
      setVersion(res.version);
      setMessage({ tone: 'success', text: `Saved as plan version ${res.version}.` });
      setAlloc(await investmentPlanService.allocation());
    } catch (e: any) {
      setMessage({ tone: 'error', text: e?.message ?? 'Failed to save the plan' });
    } finally {
      setSaving(false);
    }
  };

  const dryRun = async () => {
    setCheckSummary('Running…');
    try {
      const r: any = await investmentPlanService.check(true);
      const n = (r.recommendations_created ?? []).length;
      setCheckSummary(`Dry run: ${(r.candidates ?? []).length} candidate(s), ${n} would pass Jev's gate` +
        (r.gate?.skipped ? ` (${r.gate.skipped})` : '') + '. Nothing was saved.');
    } catch (e: any) {
      setCheckSummary(e?.message ?? 'Check failed');
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;
  }

  const accounts = (alloc?.allocation?.accounts ?? []).filter((a) => a.type !== 'Debt' || a.included);
  const err = (k: string) => errors[k];

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} gap={1} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>Investment plan</Typography>
          <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
            {version ? `Version ${version}` : 'Not saved yet'} · checked every night after the 03:00 sync
          </Typography>
        </Box>
        <FormControlLabel
          control={<Switch checked={form.enabled} onChange={(e) => set({ enabled: e.target.checked })} />}
          label={form.enabled ? 'Nightly check on' : 'Nightly check off'}
        />
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }} data-testid="plan-disclaimer">{alloc?.disclaimer || DISCLAIMER}</Alert>
      {message && <Alert severity={message.tone} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1 }}>
                <Typography variant="h6" component="h2">Target allocation</Typography>
                <Chip size="small" variant="outlined" label={`Total ${sum}%`} data-testid="target-sum"
                  sx={{ fontWeight: 600, color: sum === 100 ? 'var(--inflow-text)' : 'var(--danger-text)',
                    borderColor: sum === 100 ? 'var(--inflow-text)' : 'var(--danger-text)' }} />
              </Stack>
              {ASSET_CLASSES.map((c) => (
                <Box key={c} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Typography sx={{ width: 110, flexShrink: 0 }}>{CLASS_LABEL[c]}</Typography>
                  <Slider
                    aria-label={`${CLASS_LABEL[c]} target`}
                    value={parseFloat(form.targets[c]) || 0}
                    min={0} max={100} step={1}
                    onChange={(_, v) => set({ targets: { ...form.targets, [c]: String(v) } })}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    size="small" sx={{ width: 90 }} value={form.targets[c]}
                    inputProps={{ 'aria-label': `${CLASS_LABEL[c]} percent`, inputMode: 'decimal' }}
                    error={!!err(`targets.${c}`)}
                    onChange={(e) => set({ targets: { ...form.targets, [c]: e.target.value } })}
                    InputProps={{ endAdornment: <Typography variant="caption">%</Typography> }}
                  />
                </Box>
              ))}
              {errors.targets && <Alert severity="error" sx={{ mt: 1 }}>{errors.targets}</Alert>}
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={6} sm={6}>
                  <TextField fullWidth size="small" label="Band (± pts)" value={form.bandPts}
                    error={!!err('bandPts')} helperText={err('bandPts')} onChange={(e) => set({ bandPts: e.target.value })} />
                </Grid>
                <Grid item xs={6} sm={6}>
                  <TextField fullWidth size="small" label="Cash floor" value={form.cashFloor}
                    error={!!err('cashFloor')} helperText={err('cashFloor') || 'Never investable'}
                    onChange={(e) => set({ cashFloor: e.target.value })} />
                </Grid>
                <Grid item xs={4} sm={4}>
                  <TextField fullWidth size="small" label="Crypto cap %" value={form.caps.crypto}
                    error={!!err('caps.crypto')} onChange={(e) => set({ caps: { ...form.caps, crypto: e.target.value } })} />
                </Grid>
                <Grid item xs={4} sm={4}>
                  <TextField fullWidth size="small" label="Commodity cap %" value={form.caps.commodity}
                    error={!!err('caps.commodity')} onChange={(e) => set({ caps: { ...form.caps, commodity: e.target.value } })} />
                </Grid>
                <Grid item xs={4} sm={4}>
                  <TextField fullWidth size="small" label="Private cap %" value={form.caps.private}
                    error={!!err('caps.private')} onChange={(e) => set({ caps: { ...form.caps, private: e.target.value } })} />
                </Grid>
                <Grid item xs={4} sm={4}>
                  <TextField fullWidth size="small" label="Single-stock cap %" value={form.caps.singleStock}
                    error={!!err('caps.singleStock')} onChange={(e) => set({ caps: { ...form.caps, singleStock: e.target.value } })} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" component="h2">DCA glide path</Typography>
                <FormControlLabel control={<Switch checked={form.dcaOn} onChange={(e) => set({ dcaOn: e.target.checked })} />}
                  label={form.dcaOn ? 'On' : 'Off'} />
              </Stack>
              <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mb: 2 }}>
                Cash being deployed gradually is planned, not drift: the check reports on track, behind or ahead of this schedule.
              </Typography>
              {form.dcaOn && (
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={4}>
                    <TextField fullWidth size="small" label="Pool amount" value={form.dca.autoPool ? '' : form.dca.pool}
                      disabled={form.dca.autoPool} error={!!err('dca.pool')}
                      helperText={err('dca.pool') || (form.dca.autoPool ? 'Auto: cash above target, after reserves' : '')}
                      onChange={(e) => set({ dca: { ...form.dca, pool: e.target.value } })} />
                    <FormControlLabel control={<Switch size="small" checked={form.dca.autoPool}
                      onChange={(e) => set({ dca: { ...form.dca, autoPool: e.target.checked } })} />} label="Auto pool" />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <TextField fullWidth size="small" type="date" label="Start date" InputLabelProps={{ shrink: true }}
                      value={form.dca.startDate} error={!!err('dca.startDate')} helperText={err('dca.startDate')}
                      onChange={(e) => set({ dca: { ...form.dca, startDate: e.target.value } })} />
                  </Grid>
                  {form.streams.length === 0 && (
                    <>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth size="small" select label="Schedule source" value={form.dca.source}
                      onChange={(e) => set({ dca: { ...form.dca, source: e.target.value as 'plan' | 'external' } })}>
                      <MenuItem value="plan">This plan's rate</MenuItem>
                      <MenuItem value="external">External (qtrade planner)</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <TextField fullWidth size="small" label="Rate" value={form.dca.rate} error={!!err('dca.rate')}
                      helperText={err('dca.rate') || (form.dca.source === 'external' ? 'Fallback when no schedule is posted' : '')}
                      onChange={(e) => set({ dca: { ...form.dca, rate: e.target.value } })} />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <TextField fullWidth size="small" select label="Per" value={form.dca.rateUnit}
                      onChange={(e) => set({ dca: { ...form.dca, rateUnit: e.target.value as 'trading_day' | 'month' } })}>
                      <MenuItem value="trading_day">Trading day (TSX)</MenuItem>
                      <MenuItem value="month">Month</MenuItem>
                    </TextField>
                  </Grid>
                    </>
                  )}
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth size="small" type="date" label="Target end" InputLabelProps={{ shrink: true }}
                      value={form.dca.targetEnd} onChange={(e) => set({ dca: { ...form.dca, targetEnd: e.target.value } })} />
                  </Grid>
                </Grid>
              )}
              {alloc?.dca?.configured && (
                <Box sx={{ mt: 2 }}>
                  <DCAStatusLine dca={alloc.dca} line={alloc.dca_line} />
                  <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                    {alloc.dca.schedule_source ? `Schedule: ${alloc.dca.schedule_source} · ` : ''}
                    {baseMoney(alloc.dca.remaining, 0)} left · projected end {alloc.dca.projected_end ?? '—'}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {form.dcaOn && (
            <StreamsCard streams={form.streams} statuses={alloc?.dca?.streams} pace={alloc?.dca?.pace}
              errors={errors} onChange={(streams) => set({ streams })}
              onPause={async (id, paused) => {
                try {
                  await investmentPlanService.pauseStream(id, paused);
                  await load();
                } catch (e: any) {
                  setMessage({ tone: 'error', text: e?.message ?? 'Failed to update the stream' });
                }
              }} />
          )}

          <RemindersCard institutions={Array.from(new Set((alloc?.allocation?.accounts ?? [])
            .filter((a) => a.type === 'Investment').map((a) => a.institution ?? '').filter(Boolean)))} />

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" component="h2" sx={{ mb: 1 }}>Signals & recommendations</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={8}>
                  <TextField fullWidth size="small" label="Watchlist (symbols)" value={form.watchlist}
                    placeholder="NVDA, SHOP.TO" onChange={(e) => set({ watchlist: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label="Expiry (days)" value={form.expiryDays}
                    error={!!err('expiryDays')} helperText={err('expiryDays')} onChange={(e) => set({ expiryDays: e.target.value })} />
                </Grid>
              </Grid>
              <FormControlLabel control={<Switch checked={form.signals} onChange={(e) => set({ signals: e.target.checked })} />}
                label="Weekly-watchlist stock signals (news + Jev)" />
              <FormControlLabel control={<Switch checked={form.enrich} onChange={(e) => set({ enrich: e.target.checked })} />}
                label="Rewrite reasons with my LLM (optional)" />
            </CardContent>
          </Card>

          <Stack direction="row" gap={1} flexWrap="wrap">
            <Button variant="contained" onClick={save} disabled={!valid || saving}>
              {saving ? 'Saving…' : 'Save plan'}
            </Button>
            <Button variant="outlined" onClick={dryRun} disabled={!version}>Run check (dry run)</Button>
          </Stack>
          {checkSummary && <Alert severity="info" sx={{ mt: 2 }}>{checkSummary}</Alert>}
        </Grid>

        <Grid item xs={12} md={5}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" component="h2">Now vs target</Typography>
              {alloc?.allocation ? (
                <>
                  <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mb: 2 }}>
                    Investable {baseMoney(alloc.allocation.total, 0)} · planned DCA cash {baseMoney(alloc.allocation.cash.planned, 0)}
                  </Typography>
                  <AllocationBars classes={alloc.allocation.classes} />
                  {(alloc.allocation.unpriced ?? []).length > 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }} data-testid="unpriced">
                      Not classified (holdings incomplete or stale): {baseMoney(alloc.allocation.unpriced_value ?? '0', 0)} —{' '}
                      {(alloc.allocation.unpriced ?? []).map((u) => `${u.name} ${baseMoney(u.amount, 0)}`).join(' · ')}.
                      Left out of cash and the allocation.
                    </Alert>
                  )}
                  {(alloc.allocation.fixed_income ?? []).length > 0 && (
                    <Box sx={{ mt: 2 }} aria-label="Fixed income breakdown">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Fixed income by type</Typography>
                      {(alloc.allocation.fixed_income ?? []).map((f) => (
                        <Typography key={f.sub_class} variant="caption" sx={{ display: 'block', color: 'var(--text-secondary)' }}>
                          {f.sub_class.replace(/_/g, ' ')}: {baseMoney(f.value, 0)} · {f.pct_of_class}% of fixed income
                        </Typography>
                      ))}
                    </Box>
                  )}
                  {(alloc.allocation.options ?? []).length > 0 && (
                    <Box sx={{ mt: 2 }} aria-label="Options">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Options (not in the allocation)</Typography>
                      {(alloc.allocation.options ?? []).map((o) => (
                        <Typography key={o.symbol} variant="caption" sx={{ display: 'block', color: o.expired ? 'var(--warn-text)' : 'var(--text-secondary)' }}>
                          {o.underlying ?? o.symbol} {o.expiry} {o.strike} {o.type} × {o.quantity} · notional {baseMoney(o.notional, 0)}
                          {o.expired ? ' · past expiry: stale broker data' : ''}
                        </Typography>
                      ))}
                    </Box>
                  )}
                  {(alloc.cap_alerts ?? []).map((w) => <Alert key={w} severity="error" sx={{ mt: 2 }}>{w}</Alert>)}
                  {(alloc.warnings ?? []).map((w) => <Alert key={w} severity="warning" sx={{ mt: 2 }}>{w}</Alert>)}
                  {alloc.allocation.unclassified.length > 0 && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'var(--text-secondary)' }}>
                      No positions yet (left out of the allocation, {baseMoney(alloc.allocation.unclassified_value ?? '0', 0)}): {alloc.allocation.unclassified.join(', ')}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>Save a plan to see the comparison.</Typography>
              )}
            </CardContent>
          </Card>
          <Card sx={{ mb: 3 }} component="section" aria-label="Reserves and transfers">
            <CardContent>
              <Typography variant="h6" component="h2">Reserves & pending transfers</Typography>
              <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mb: 1 }}>
                Reserves are never deployed and stay out of the pool and drift.
              </Typography>
              {form.reserves.length === 0 && <Typography variant="body2">No reserves (cash floor {baseMoney(form.cashFloor || 0, 0)}).</Typography>}
              {form.reserves.map((r) => (
                <Typography key={r.name} variant="body2">{r.name}: <strong>{baseMoney(r.amount, 0)}</strong></Typography>
              ))}
              {(alloc?.allocation?.cash?.below_floor) && (
                <Alert severity="error" sx={{ mt: 1 }}>Liquid cash is below the reserves by {baseMoney(alloc.allocation.cash.shortfall, 0)}.</Alert>
              )}
              {(alloc?.transfers ?? []).map((t) => (
                <Box key={t.id ?? t.from_account_id} sx={{ mt: 1.5 }}>
                  <Stack direction="row" gap={1} alignItems="center">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{t.account_name} → {t.to_institution}</Typography>
                    <Chip size="small" variant="outlined" label={t.detected ? `done ${t.detected_on}` : t.effective_status.replace('_', ' ')} />
                  </Stack>
                  <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                    ≈ {Number(t.estimated_cash_native).toLocaleString(undefined, { maximumFractionDigits: 0 })} {t.currency} uninvested
                    {t.note ? ` · ${t.note}` : ''}
                  </Typography>
                </Box>
              ))}
              {(alloc?.allocation?.cash_by_currency ?? []).length > 1 && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1.5 }}>
                  Cash by currency: {(alloc?.allocation?.cash_by_currency ?? []).map((c) =>
                    `${c.currency} ${Number(c.native).toLocaleString(undefined, { maximumFractionDigits: 0 })}`).join(' · ')}
                </Typography>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography variant="h6" component="h2">Account scope</Typography>
              <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mb: 1 }}>
                Investable assets only: property, vehicles and their loans are left out by default.
              </Typography>
              <Box sx={{ maxHeight: 420, overflow: 'auto' }}>
                {accounts.map((a) => {
                  const excluded = form.excluded.includes(a.id);
                  const pinned = rules.find((r) => r.account_id === a.id)?.asset_class ?? '';
                  return (
                    <Box key={a.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FormControlLabel sx={{ flex: 1, m: 0, minWidth: 0 }}
                        control={<Checkbox size="small" checked={a.included && !excluded}
                          disabled={!a.included && !excluded}
                          onChange={(e) => set({ excluded: e.target.checked ? form.excluded.filter((x) => x !== a.id) : [...form.excluded, a.id] })} />}
                        label={<Typography variant="body2">{a.name} <span style={{ color: 'var(--text-secondary)' }}>
                          · {baseMoney(a.balance_base, 0)}{!a.included ? ` · ${a.reason}` : ''}
                          {a.included && a.cash_source ? ` · cash ${baseMoney(a.cash ?? 0, 0)} (${a.cash_source})` : ''}
                          {a.included && a.unpriced && parseFloat(a.unpriced) !== 0 ? ` · unpriced ${baseMoney(a.unpriced, 0)}` : ''}
                          {a.included && a.source === 'unclassified' ? ' · no positions: pick a class' : ''}</span></Typography>} />
                      {a.type === 'Investment' && a.included && (
                        <TextField select size="small" value={pinned} sx={{ width: 130 }} SelectProps={{ native: true }}
                          inputProps={{ 'aria-label': `${a.name} class` }}
                          onChange={async (e) => {
                            try {
                              const res = await investmentPlanService.setAccountClass(a.id, e.target.value as AssetClass | '');
                              setRules(res.plan?.accounts ?? []);
                              setAlloc(await investmentPlanService.allocation());
                            } catch (err: any) {
                              setMessage({ tone: 'error', text: err?.message ?? 'Failed to set the class' });
                            }
                          }}>
                          <option value="">From holdings</option>
                          {ASSET_CLASSES.map((c) => <option key={c} value={c}>{CLASS_LABEL[c]}</option>)}
                        </TextField>
                      )}
                    </Box>
                  );
                })}
                {accounts.length === 0 && <Typography variant="body2">Accounts appear after the first save.</Typography>}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
